import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { FormQuestion, QuestionType, ChoiceOption } from '@affogato/shared';

interface SectionEditorProps {
  question: FormQuestion;
  questionIndex: number;
  totalQuestions: number;
  readOnly?: boolean;
  onUpdate: (updates: Partial<FormQuestion>) => void;
  onDelete: () => void;
  onMove: (direction: 'up' | 'down') => void;
}

const questionTypes: QuestionType[] = ['scale', 'yes_no', 'text', 'single_choice', 'multiple_choice'];

export function SectionEditor({
  question,
  questionIndex,
  totalQuestions,
  readOnly = false,
  onUpdate,
  onDelete,
  onMove,
}: SectionEditorProps) {
  const { t } = useTranslation('diagnostics');
  const [isExpanded, setIsExpanded] = useState(true);

  const handleTypeChange = (type: QuestionType) => {
    const updates: Partial<FormQuestion> = { type };

    // Set default configs based on type
    if (type === 'scale') {
      updates.scaleConfig = question.scaleConfig || {
        min: 1,
        max: 5,
        minLabel: '',
        maxLabel: '',
      };
      updates.choices = undefined;
    } else if (type === 'single_choice' || type === 'multiple_choice') {
      updates.choices = question.choices?.length
        ? question.choices
        : [
            { id: crypto.randomUUID(), text: '', value: 1 },
            { id: crypto.randomUUID(), text: '', value: 2 },
          ];
      updates.scaleConfig = undefined;
    } else {
      updates.scaleConfig = undefined;
      updates.choices = undefined;
    }

    onUpdate(updates);
  };

  const addChoice = () => {
    const choices = question.choices || [];
    onUpdate({
      choices: [
        ...choices,
        { id: crypto.randomUUID(), text: '', value: choices.length + 1 },
      ],
    });
  };

  const updateChoice = (choiceId: string, updates: Partial<ChoiceOption>) => {
    onUpdate({
      choices: question.choices?.map((c) =>
        c.id === choiceId ? { ...c, ...updates } : c
      ),
    });
  };

  const deleteChoice = (choiceId: string) => {
    onUpdate({
      choices: question.choices?.filter((c) => c.id !== choiceId),
    });
  };

  return (
    <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
        >
          <ChevronRightIcon
            className={clsx(
              'w-4 h-4 text-gray-500 dark:text-slate-400 transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </button>

        <span className="text-xs font-medium text-gray-400 dark:text-slate-500 w-6">
          {questionIndex + 1}.
        </span>

        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={question.text}
            onChange={(e) => onUpdate({ text: e.target.value })}
            disabled={readOnly}
            placeholder={t('builder.questionText')}
            className="w-full bg-transparent border-0 p-0 text-sm font-medium text-gray-900 dark:text-white focus:ring-0 placeholder-gray-400 dark:placeholder-slate-500"
          />
        </div>

        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onMove('up')}
              disabled={questionIndex === 0}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
            >
              <ChevronUpIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
            </button>
            <button
              onClick={() => onMove('down')}
              disabled={questionIndex === totalQuestions - 1}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
            >
              <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
            </button>
            <button
              onClick={onDelete}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              <TrashIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
            </button>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-gray-200 dark:border-slate-600">
          {/* Description */}
          <div>
            <input
              type="text"
              value={question.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              disabled={readOnly}
              placeholder={t('builder.questionDescription')}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>

          {/* Type & Required Row */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                {t('builder.questionType')}
              </label>
              <select
                value={question.type}
                onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
                disabled={readOnly}
                className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {questionTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`builder.questionTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                disabled={readOnly}
                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-slate-300">
                {t('builder.required')}
              </span>
            </label>
          </div>

          {/* Scale Config */}
          {question.type === 'scale' && question.scaleConfig && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  {t('builder.scaleLabels.min')}
                </label>
                <input
                  type="text"
                  value={question.scaleConfig.minLabel || ''}
                  onChange={(e) =>
                    onUpdate({
                      scaleConfig: { ...question.scaleConfig!, minLabel: e.target.value },
                    })
                  }
                  disabled={readOnly}
                  placeholder="1 - Muy bajo"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  {t('builder.scaleLabels.max')}
                </label>
                <input
                  type="text"
                  value={question.scaleConfig.maxLabel || ''}
                  onChange={(e) =>
                    onUpdate({
                      scaleConfig: { ...question.scaleConfig!, maxLabel: e.target.value },
                    })
                  }
                  disabled={readOnly}
                  placeholder="5 - Muy alto"
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
                />
              </div>
            </div>
          )}

          {/* Choices */}
          {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400">
                {t('builder.choices.title')}
              </label>
              {question.choices?.map((choice, idx) => (
                <div key={choice.id} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 dark:text-slate-500 w-4">
                    {idx + 1}.
                  </span>
                  <input
                    type="text"
                    value={choice.text}
                    onChange={(e) => updateChoice(choice.id, { text: e.target.value })}
                    disabled={readOnly}
                    placeholder={t('builder.choices.text')}
                    className="flex-1 px-3 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
                  />
                  <input
                    type="number"
                    value={choice.value}
                    onChange={(e) => updateChoice(choice.id, { value: Number(e.target.value) })}
                    disabled={readOnly}
                    placeholder={t('builder.choices.value')}
                    className="w-16 px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 text-center focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {!readOnly && question.choices && question.choices.length > 2 && (
                    <button
                      onClick={() => deleteChoice(choice.id)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button
                  onClick={addChoice}
                  className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t('builder.choices.add')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
